import type { PageRef } from '@shared/api';

/**
 * A running row's label plus the page chip that goes with it. Held and
 * advanced as one unit — the page chip follows whichever label is on
 * screen, not the newest one to arrive.
 */
export interface StatusQueueEntry {
  label: string;
  page: PageRef | null;
}

/** Every status label stays on screen at least this long (the fast-tool flicker fix). */
export const STATUS_MIN_DISPLAY_MS = 1200;

export interface StatusQueueState {
  /** The entry currently on screen. */
  shown: StatusQueueEntry;
  /** When `shown` went up, same clock as the `now` passed to the functions below. */
  shownAt: number;
  /** The newest entry to arrive while `shown` hasn't hit the minimum yet, or null. */
  pending: StatusQueueEntry | null;
}

function pagesEqual(a: PageRef | null, b: PageRef | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.sectionId === b.sectionId && a.title === b.title && a.number === b.number;
}

function entriesEqual(a: StatusQueueEntry, b: StatusQueueEntry): boolean {
  return a.label === b.label && pagesEqual(a.page, b.page);
}

export function initStatusQueue(entry: StatusQueueEntry, now: number): StatusQueueState {
  return { shown: entry, shownAt: now, pending: null };
}

/**
 * A new entry arrived at `now`. `shown` has been up for at least
 * `minDisplayMs` -> it's replaced right away. Otherwise the new entry is
 * queued, replacing whatever was already queued — only the newest arrival
 * during a hold ever gets shown, so the row never lags more than one label
 * behind (intermediates are skipped).
 */
export function arriveStatus(
  state: StatusQueueState,
  entry: StatusQueueEntry,
  now: number,
  minDisplayMs: number = STATUS_MIN_DISPLAY_MS,
): StatusQueueState {
  if (entriesEqual(state.shown, entry)) {
    // Nothing new to show. Also nothing left worth holding, if a pending
    // entry is now just a repeat of what's already on screen.
    return state.pending ? { ...state, pending: null } : state;
  }
  if (now - state.shownAt >= minDisplayMs) {
    return { shown: entry, shownAt: now, pending: null };
  }
  if (state.pending && entriesEqual(state.pending, entry)) return state;
  return { ...state, pending: entry };
}

/** The minimum has elapsed since `shown` went up — promote the pending entry, if any. */
export function advanceStatusQueue(state: StatusQueueState, now: number): StatusQueueState {
  if (!state.pending) return state;
  return { shown: state.pending, shownAt: now, pending: null };
}

/** The task ended — drop anything held back immediately, so it never flashes after the row leaves running state. */
export function clearPendingStatus(state: StatusQueueState): StatusQueueState {
  return state.pending ? { ...state, pending: null } : state;
}
